import { useQuery } from '@tanstack/react-query'

import axios from '@/_lib/axiosInstance'
import { ApiResponse, IPriceListItem, ITemplate } from '@/_types'

/**
 * E-commerce product list payload for a single ambulance.
 *
 * @property priceListItems - Standalone price-list services.
 * @property templates - Bookable doctor/service templates with timeslots.
 */
interface IGetProductsResponse {
	priceListItems: IPriceListItem[]
	templates: ITemplate[]
}

/**
 * Fetches templates and price-list items available for ordering at the given ambulance.
 *
 * @param ambulanceId - Primary key of the ambulance from step 1.
 * @returns Wrapped API response with {@link IGetProductsResponse}.
 */
export const fetchProducts = async (
	ambulanceId: number
): Promise<ApiResponse<IGetProductsResponse>> => {
	const queryParams = new URLSearchParams()
	queryParams.append('ambulanceId', ambulanceId.toString())

	return axios
		.get<ApiResponse<IGetProductsResponse>>(
			`v1/web/ecommerce/products?${queryParams.toString()}`
		)
		.then(res => res.data)
}

/**
 * React Query hook for {@link fetchProducts}; disabled until `ambulanceId` is set.
 *
 * @param ambulanceId - Ambulance id or `null` while the parent step is still resolving the slug.
 */
export const useGetProducts = (ambulanceId: number | null) => {
	return useQuery<ApiResponse<IGetProductsResponse>>({
		queryKey: ['products', ambulanceId],
		queryFn: () => fetchProducts(ambulanceId!),
		enabled: !!ambulanceId,
		select: data => data
	})
}
